import { useId, useRef, useState } from 'react';
import { Box, Button, Checkbox, Chip, Divider, Grow, Modal, Paper, FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, Switch, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import { DashboardYearFilter } from '../components/DashboardYearFilter';
import { useGPASubmissionData } from '../contexts/GPASubmissionDataContext';
import { useDashboardSchoolYear } from '../contexts/DashboardSchoolYearContext';
import { GPASubmissionFilter } from './types';

const values = (event: SelectChangeEvent<number[]>) => typeof event.target.value === 'string'
    ? event.target.value.split(',').map(Number) : event.target.value;

interface Props {
    showFileId?: boolean;
    onShowFileIdChange?: (showFileId: boolean) => void;
    usePopup?: boolean;
}

export const GPASubmissionFilterControls = ({ showFileId, onShowFileIdChange, usePopup = false }: Props = {}) => {
    const { filter: appliedFilter, updateFilter: updateAppliedFilter, activeSchoolYear, schools, gradeLevels, termTypes } = useGPASubmissionData();
    const { schoolYear } = useDashboardSchoolYear();
    const id = useId();
    const popupAnchor = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [draftFilter, setDraftFilter] = useState(appliedFilter);
    const [draftShowFileId, setDraftShowFileId] = useState(false);
    const filter = usePopup ? draftFilter : appliedFilter;
    const updateFilter = (updates: Partial<GPASubmissionFilter>) => {
        if (usePopup) setDraftFilter(previous => ({ ...previous, ...updates }));
        else updateAppliedFilter(updates);
    };
    const maxTermCount = termTypes.filter(type => !filter.termTypeIds.length || filter.termTypeIds.includes(type.id))
        .reduce((maximum, type) => Math.max(maximum, type.termCount || 0), 0);
    const termNumbers = Array.from({ length: maxTermCount }, (_, index) => index + 1);
    const academicControls = <>
        <FormControl size="small" sx={{ minWidth: 190 }}><InputLabel id={`${id}-schools-label`}>Schools</InputLabel>
            <Select multiple labelId={`${id}-schools-label`} value={filter.schoolIds} label="Schools" onChange={event => updateFilter({ schoolIds: values(event) })}
                renderValue={selected => selected.length ? `${selected.length} selected` : 'All Schools'}>
                {schools.map(option => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel id={`${id}-grades-label`}>Grade Levels</InputLabel>
            <Select multiple labelId={`${id}-grades-label`} value={filter.gradeLevelIds} label="Grade Levels" onChange={event => updateFilter({ gradeLevelIds: values(event) })}
                renderValue={selected => selected.length ? `${selected.length} selected` : 'All Grade Levels'}>
                {gradeLevels.map(option => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
            </Select>
        </FormControl>
    </>;
    const termControls = <>
        <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel id={`${id}-term-types-label`}>Term Types</InputLabel>
            <Select multiple labelId={`${id}-term-types-label`} value={filter.termTypeIds} label="Term Types" onChange={event => updateFilter({ termTypeIds: values(event), termNumbers: [] })}
                renderValue={selected => selected.length ? `${selected.length} selected` : 'All Term Types'}>
                {termTypes.map(option => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel id={`${id}-term-numbers-label`}>Term Numbers</InputLabel>
            <Select multiple labelId={`${id}-term-numbers-label`} value={filter.termNumbers} label="Term Numbers"
                onChange={event => updateFilter({ termNumbers: values(event) })}
                renderValue={selected => selected.length ? selected.join(', ') : 'All Terms'}>
                {termNumbers.map(number => <MenuItem key={number} value={number}>{number}</MenuItem>)}
            </Select>
        </FormControl>
    </>;
    const identificationControl = onShowFileIdChange && <FormControlLabel
        label="File ID"
        sx={{ ml: 0.5, mr: 0 }}
        control={<Switch size="small" checked={usePopup ? draftShowFileId : showFileId || false}
            onChange={event => usePopup ? setDraftShowFileId(event.target.checked) : onShowFileIdChange(event.target.checked)} />}
    />;

    if (!usePopup) return <DashboardYearFilter idPrefix="gpa-submission" years={filter.schoolYears} activeSchoolYear={activeSchoolYear}
        onApply={schoolYears => updateFilter({ schoolYears })}>
        {academicControls}{termControls}{identificationControl}
    </DashboardYearFilter>;

    // File ID changes column visibility only; it does not restrict the results.
    const activeCount = [appliedFilter.schoolYears, appliedFilter.schoolIds, appliedFilter.gradeLevelIds,
        appliedFilter.termTypeIds, appliedFilter.termNumbers].filter(selected => selected.length > 0).length;
    const currentYear = activeSchoolYear || new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, index) => currentYear - index);
    const close = () => {
        setOpen(false);
        setDraftFilter(appliedFilter);
        setDraftShowFileId(showFileId || false);
    };
    const sectionStyle = { display: 'flex', flexDirection: 'column', gap: 1.5, py: 2 };
    const headingStyle = { display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 0.5 };

    return <Box ref={popupAnchor} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<FilterListIcon />} onClick={() => {
            setDraftFilter({ ...appliedFilter });
            setDraftShowFileId(showFileId || false);
            setOpen(true);
        }}>Filters ({activeCount})</Button>
        <Modal open={open} onClose={close} closeAfterTransition disablePortal disableScrollLock
            container={() => popupAnchor.current?.parentElement || document.body}
            BackdropProps={{ sx: { position: 'absolute', bgcolor: 'rgba(0, 0, 0, 0.18)' }, timeout: 200 }}
            sx={{ position: 'absolute', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
            <Grow in={open} timeout={{ enter: 240, exit: 180 }} style={{ transformOrigin: 'right center' }}>
                <Paper role="dialog" aria-modal="true" aria-labelledby="gpa-submission-filter-title" tabIndex={-1}
                    elevation={8} sx={{ width: 440, maxWidth: '100%', maxHeight: '100%', display: 'flex',
                        flexDirection: 'column', overflow: 'hidden', borderRadius: 2, outline: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, flexShrink: 0 }}>
                        <Typography id="gpa-submission-filter-title" variant="h6">Filters</Typography>
                        <IconButton aria-label="Close GPA filters" onClick={close}><CloseIcon /></IconButton>
                    </Box>
                    <Divider />
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3 }}>
                        <Box sx={sectionStyle}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" sx={headingStyle}><SchoolIcon fontSize="small" />Academic</Typography>
                                <Button size="small" aria-label="Clear academic filters" onClick={() => updateFilter({ schoolYears: [schoolYear || activeSchoolYear || currentYear], schoolIds: [], gradeLevelIds: [] })}>Clear</Button>
                            </Box>
                            <FormControl size="small">
                                <InputLabel id="gpa-submission-popup-year-label">School Year</InputLabel>
                                <Select labelId="gpa-submission-popup-year-label" multiple value={filter.schoolYears}
                                    onChange={event => updateFilter({ schoolYears: values(event) })}
                                    input={<OutlinedInput label="School Year" />}
                                    renderValue={selected => <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap' }}>
                                        {selected.map(year => <Chip key={year} label={`${year}-${year + 1}`} size="small" />)}
                                    </Box>}>
                                    {years.map(year => <MenuItem key={year} value={year}>
                                        <Checkbox checked={filter.schoolYears.includes(year)} /><ListItemText primary={`${year}-${year + 1}`} />
                                    </MenuItem>)}
                                </Select>
                            </FormControl>
                            {academicControls}
                        </Box>
                        <Divider />
                        <Box sx={sectionStyle}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" sx={headingStyle}><CalendarTodayIcon fontSize="small" />Term</Typography>
                                <Button size="small" aria-label="Clear term filters" onClick={() => updateFilter({ termTypeIds: [], termNumbers: [] })}>Clear</Button>
                            </Box>
                            {termControls}
                        </Box>
                        {onShowFileIdChange && <><Divider /><Box sx={sectionStyle}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" sx={headingStyle}><DescriptionIcon fontSize="small" />Identification</Typography>
                                <Button size="small" aria-label="Clear identification filters" onClick={() => setDraftShowFileId(false)}>Clear</Button>
                            </Box>
                            {identificationControl}
                        </Box></>}
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, flexShrink: 0 }}>
                        <Button size="small" onClick={() => {
                            setDraftFilter({ schoolYears: [schoolYear || activeSchoolYear || currentYear], schoolIds: [], gradeLevelIds: [], termTypeIds: [], termNumbers: [] });
                            setDraftShowFileId(false);
                        }}>Reset</Button>
                        <Box sx={{ flex: 1 }} />
                        <Button color="inherit" onClick={close}>Cancel</Button>
                        <Button variant="contained" disabled={!filter.schoolYears.length} onClick={() => {
                            if (JSON.stringify(draftFilter) !== JSON.stringify(appliedFilter)) updateAppliedFilter(draftFilter);
                            onShowFileIdChange?.(draftShowFileId);
                            setOpen(false);
                        }}>Apply Filters</Button>
                    </Box>
                </Paper>
            </Grow>
        </Modal>
    </Box>;
};
